import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Tag, RefreshCw, X, Copy, Info, Users, Clock, Percent, Gift, Database } from "lucide-react";

/**
 * Offer Codes page (v29.12 Phase 2).
 *
 * MariaDB `promo_codes` is the SINGLE SOURCE OF TRUTH — no manual
 * creation is allowed in Connect. Admins tap "Sync from MariaDB" to
 * pull the latest codes; the auto-sync tick runs every 5 minutes.
 * Legacy manually-created rows (from before Phase 2) stay listed but
 * are marked "Legacy — retiring" and are the only rows the admin can
 * still edit/deactivate manually.
 */
const fmtDate = (s) => (s ? new Date(s).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—");
const relative = (s) => {
  if (!s) return "";
  const diff = (new Date(s) - Date.now()) / 1000;
  const abs = Math.abs(diff);
  const dir = diff >= 0 ? "in" : "ago";
  if (abs < 60) return `${Math.round(abs)}s ${dir}`;
  if (abs < 3600) return `${Math.round(abs / 60)}m ${dir}`;
  if (abs < 86400) return `${Math.round(abs / 3600)}h ${dir}`;
  return `${Math.round(abs / 86400)}d ${dir}`;
};

export default function Offers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [grantsFor, setGrantsFor] = useState(null);
  const [grants, setGrants] = useState([]);
  // v29.12 Phase 2 — MariaDB sync
  const [syncing, setSyncing] = useState(false);
  const [syncHealth, setSyncHealth] = useState(null);

  const loadHealth = async () => {
    try {
      const { data } = await api.get("/offers/sync-status");
      setSyncHealth(data.health || {});
    } catch { /* ignored */ }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/offers");
      setRows(data);
      loadHealth();
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const runSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/offers/sync-from-mariadb");
      const created = data.created ?? 0;
      const updated = data.updated ?? 0;
      const deactivated = data.deactivated ?? 0;
      toast.success(`Synced ${data.pulled} promos · ${created} new · ${updated} updated · ${deactivated} deactivated`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally { setSyncing(false); }
  };
  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success(`Copied ${code}`);
    } catch { toast.error("Copy failed"); }
  };

  const openGrants = async (offer) => {
    setGrantsFor(offer);
    try {
      const { data } = await api.get(`/offers/${offer.id}/grants`);
      setGrants(data);
    } catch { setGrants([]); }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="offers-page">
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500 mb-1">Marketing</div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tighter flex items-center gap-2">
            <Gift className="h-6 w-6 text-orange-500" /> Offer Codes
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Promo codes are synced from the PrepHacker Subscription MariaDB.
            Attach them to cadence steps and the AI reveals them only to
            selected users — a server-side leak check strips any accidental
            reveal before send.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {syncHealth?.promos?.at && (
            <div className="text-[10px] text-slate-500 text-right leading-tight">
              <div className="uppercase tracking-widest font-semibold">Last sync</div>
              <div>{relative(syncHealth.promos.at)}</div>
            </div>
          )}
          <button
            data-testid="offers-sync-btn"
            onClick={runSync}
            disabled={syncing}
            className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-500 disabled:opacity-60 text-white text-xs font-semibold px-4 py-2 rounded-md transition-colors"
          >
            <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Syncing…" : "Sync from MariaDB"}
          </button>
        </div>
      </div>

      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 flex items-start gap-3 mb-6">
        <Info className="h-4 w-4 text-indigo-600 shrink-0 mt-0.5" />
        <div className="text-xs text-indigo-900 leading-relaxed">
          <b>MariaDB is the source of truth.</b> Create promo codes in the
          Subscription admin panel and they&apos;ll appear here automatically
          within 5 minutes — or tap <b>Sync from MariaDB</b> to pull immediately.
          MariaDB owns the discount, validity, usage limit, and active/inactive
          state; Connect owns only the attachment to cadences.
        </div>
      </div>

      {loading ? (
        <div className="text-center text-slate-500 py-10">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-slate-300 rounded-lg p-10 text-center text-slate-500" data-testid="offers-empty-state">
          No promo codes synced yet. Tap <b>Sync from MariaDB</b> to fetch the
          latest promos from the Subscription admin panel.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] font-semibold uppercase tracking-widest text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">Code</th>
                <th className="text-left px-4 py-3">Source</th>
                <th className="text-left px-4 py-3">Description</th>
                <th className="text-left px-4 py-3">Discount</th>
                <th className="text-left px-4 py-3">Valid until</th>
                <th className="text-left px-4 py-3">Usage</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((o) => (
                <tr
                  key={o.id}
                  data-testid={`offer-row-${o.id}`}
                  className={`border-t border-slate-100 ${!o.active ? "opacity-50" : ""}`}
                >
                  <td className="px-4 py-3 font-mono font-bold text-orange-700 flex items-center gap-2">
                    {o.code}
                    <button
                      onClick={() => copyCode(o.code)}
                      className="text-slate-400 hover:text-slate-700"
                      title="Copy"
                    ><Copy className="h-3 w-3" /></button>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      data-testid={`offer-source-mariadb-${o.id}`}
                      className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest px-2 py-1 rounded border bg-indigo-50 text-indigo-700 border-indigo-200"
                      title={`Synced from MariaDB (id: ${o.external_promo_id || "?"})`}
                    >
                      <Database className="h-3 w-3" /> MariaDB
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-700 max-w-[260px] truncate" title={o.description}>{o.description || "—"}</td>
                  <td className="px-4 py-3">
                    {o.discount_pct ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold">
                        <Percent className="h-3 w-3" /> {o.discount_pct}%
                      </span>
                    ) : o.discount_amount ? (
                      <span className="text-emerald-700 font-semibold">₹{o.discount_amount}</span>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">
                    {o.valid_until ? (
                      <>
                        <div>{fmtDate(o.valid_until)}</div>
                        <div className="text-[10px] text-slate-400">{relative(o.valid_until)}</div>
                      </>
                    ) : <span className="text-slate-400">never</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-700 tabular">
                    <button
                      onClick={() => openGrants(o)}
                      className="inline-flex items-center gap-1 hover:text-indigo-600"
                      data-testid={`offer-grants-${o.id}`}
                    >
                      <Users className="h-3 w-3" />
                      <span className="font-mono">{o.current_uses}</span>
                      {o.max_uses ? <span className="text-slate-400">/ {o.max_uses}</span> : ""}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] font-semibold uppercase tracking-widest px-2 py-1 rounded border ${
                      o.active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"
                    }`}>{o.active ? "Active" : "Inactive"}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span
                      data-testid={`offer-readonly-${o.id}`}
                      className="text-[10px] font-semibold uppercase tracking-widest text-slate-400"
                      title="Managed in MariaDB — edit in the Subscription admin panel"
                    >Read only</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Grants drawer */}
      {grantsFor && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-start justify-end p-4 z-50" onClick={() => setGrantsFor(null)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Grants for</div>
                <div className="font-mono font-bold text-orange-700">{grantsFor.code}</div>
              </div>
              <button onClick={() => setGrantsFor(null)} className="text-slate-400 hover:text-slate-800"><X className="h-4 w-4" /></button>
            </div>
            <div className="p-4 text-sm">
              {grants.length === 0 ? (
                <div className="text-slate-500 text-center py-6">No grants yet. This code hasn&apos;t been sent via any cadence.</div>
              ) : (
                <ul className="space-y-2">
                  {grants.map((g) => (
                    <li key={g.id} className="border border-slate-200 rounded-md p-3 text-xs">
                      <div className="flex items-center justify-between">
                        <div className="font-mono text-slate-700">{g.trial_user_id}</div>
                        <div className="text-slate-400 flex items-center gap-1"><Clock className="h-3 w-3" /> {relative(g.granted_at)}</div>
                      </div>
                      <div className="mt-1 text-slate-500">
                        Granted: {fmtDate(g.granted_at)} · Channel: {g.channel || "—"}
                      </div>
                      {g.revealed_at ? (
                        <div className="mt-0.5 text-emerald-700">Revealed {relative(g.revealed_at)} · reveals: {g.reveal_count}</div>
                      ) : (
                        <div className="mt-0.5 text-slate-400">Not yet revealed to user</div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
